import { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, BarChart3, Users, TrendingUp, Flame, Thermometer, Snowflake, Loader2 } from "lucide-react";

const TIER_STYLES = { HOT: "text-red-400 bg-red-900/30 border-red-800", WARM: "text-yellow-400 bg-yellow-900/30 border-yellow-800", COLD: "text-blue-400 bg-blue-900/30 border-blue-800" };
const TIER_ICONS = { HOT: Flame, WARM: Thermometer, COLD: Snowflake };

export default function LeadScout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runAnalysis = async () => {
    setLoading(true); setError("");
    try {
      const { data: result } = await API.get("/tools/lead-scout/analyze");
      setData(result);
    } catch (e) { setError(e.response?.data?.detail || "Analysis failed"); toast.error(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0]" data-testid="lead-scout-page">
      <nav className="border-b border-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase">NRD<span className="text-[#00FF41]">.</span></Link>
            <span className="text-xs font-mono text-[#00FF41]">/ LEAD-SCOUT</span>
          </div>
          <Link to="/dashboard" className="text-sm text-slate-500 hover:text-[#00FF41] flex items-center gap-1"><ArrowLeft size={14} /> Dashboard</Link>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-1">Shopify Lead-Scout</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight">B2B Lead Analytics</h1>
          </div>
          <button onClick={runAnalysis} disabled={loading} className="flex items-center gap-2 bg-[#00FF41] text-[#0A0A0A] px-6 py-3 text-sm font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50" data-testid="run-analysis-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <BarChart3 size={16} />} {loading ? "Analyzing..." : "Run Analysis"}
          </button>
        </div>
        {error && <div className="p-4 bg-red-900/30 border border-red-800 text-sm text-red-400 mb-6" data-testid="lead-scout-error">{error}</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-0 border border-[#1E1E1E] bg-[#111111] mb-8">
              {[
                { label: "Total Customers", val: data.stats.total_customers },
                { label: "Hot Leads", val: data.stats.hot_leads, color: "text-red-400" },
                { label: "Warm Leads", val: data.stats.warm_leads, color: "text-yellow-400" },
                { label: "Cold Leads", val: data.stats.cold_leads, color: "text-blue-400" },
                { label: "Avg Score", val: data.stats.avg_score },
                { label: "Total Revenue", val: `$${data.stats.total_revenue.toLocaleString()}`, color: "text-[#00FF41]" },
              ].map((s, i) => (
                <div key={i} className={`p-5 ${i < 5 ? 'border-r border-[#1E1E1E]' : ''}`}>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">{s.label}</p>
                  <p className={`font-heading text-2xl font-black ${s.color || ''}`}>{s.val}</p>
                </div>
              ))}
            </div>
            <div className="border border-[#1E1E1E] bg-[#111111]">
              <div className="border-b border-[#1E1E1E] px-5 py-3 bg-[#0A0A0A]">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Lead Pipeline ({data.leads.length} leads)</p>
              </div>
              {data.leads.map((l, i) => {
                const TierIcon = TIER_ICONS[l.tier] || Users;
                return (
                  <div key={i} className="flex items-center gap-4 px-5 py-3 border-b border-[#1E1E1E] last:border-0 hover:bg-[#0A0A0A]/50" data-testid={`lead-row-${i}`}>
                    <div className="w-10 text-center">
                      <span className={`text-xs font-mono px-2 py-0.5 border ${TIER_STYLES[l.tier]}`}>{l.score}</span>
                    </div>
                    <TierIcon size={14} className={l.tier === 'HOT' ? 'text-red-400' : l.tier === 'WARM' ? 'text-yellow-400' : 'text-blue-400'} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{l.name}</p>
                      <p className="text-xs text-slate-500 truncate">{l.email} {l.company && `• ${l.company}`}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#00FF41] font-mono">${l.total_spent.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">{l.order_count} orders</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {!data && !loading && <div className="text-center py-20 text-slate-500"><BarChart3 size={48} className="mx-auto mb-4 opacity-30" /><p>Click "Run Analysis" to scan your Shopify store</p></div>}
      </div>
    </div>
  );
}
